import { IColumn } from '@fluentui/react';
import AppContext from 'App/AppContext';
import {
  IPDetailsList,
  IPIconButton,
  IPLink,
  IPPersona,
  IPSeparator,
  IPStack,
  IPText,
  IPTooltipDelay,
  IPTooltipHost,
} from 'baseComponents';
import { IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPSelectionMode } from 'baseComponents/Utils/Selection';
import { getPermissionLabel } from 'components/Auth/AuthHelpers';
import CheckList from 'components/Checklist/Checklist';
import { globalStackTokensGapSmall, globalTextStylesBold } from 'globalStyles';
import AuditTrailEntry from 'models/auditTrailEntry';
import { TaskCheckList } from 'models/tasks/taskHelperClasses';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuditTrailActions } from 'utils/auditTrailActions';
import { toLocaleDateTime } from 'utils/datetime';
import { getHtmlJSX } from 'utils/html';
import { sortOnNumber } from 'utils/sorting';

interface ISingleAuditProps {
  audit: AuditTrailEntry;
}

export interface AuditViewItem {
  name: string;
  value: string;
  auditTrailActionId: AuditTrailActions;
  property: string;
  isNewValue: boolean | undefined;
}

const SingleAudit = (props: ISingleAuditProps) => {
  const { t } = useTranslation([
    'translation',
    'auditTrail',
    'risk',
    'control',
    'library',
    'adminAuth',
    'audit',
    'package',
    'objective',
    'asset',
    'processingActivity',
  ]);
  const appContext = useContext(AppContext);

  // Use separate copied state for old and new value
  const [copiedNew, setCopiedNew] = useState(false);
  const [copiedOld, setCopiedOld] = useState(false);

  const copyToClipboard = (text: string, isNew: boolean | undefined) => {
    if (navigator && navigator.clipboard) {
      navigator.clipboard.writeText(text);
      if (isNew) {
        setCopiedNew(true);
        setTimeout(() => setCopiedNew(false), 1200);
      } else {
        setCopiedOld(true);
        setTimeout(() => setCopiedOld(false), 1200);
      }
    }
  };

  const onRenderValue = (item: AuditViewItem): JSX.Element => {
    if (!item || !item.value) return <p />;
    let output: JSX.Element = <p />;
    let isHTML: boolean = false;
    let isBig: boolean = false;

    try {
      switch (item.auditTrailActionId) {
        case AuditTrailActions.Task_description_updated:
        case AuditTrailActions.Risk_analysis_updated:
        case AuditTrailActions.Risk_description_updated:
        case AuditTrailActions.Risk_strategy_updated:
        case AuditTrailActions.Risk_asset_updated:
        case AuditTrailActions.Control_description_updated:
        case AuditTrailActions.Control_background_updated:
        case AuditTrailActions.Control_implementation_updated:
        case AuditTrailActions.Asset_description_updated:
        case AuditTrailActions.Process_description_updated:
        case AuditTrailActions.Objective_description_updated:
        case AuditTrailActions.ProcessingActivity_purpose_updated:
        case AuditTrailActions.ProcessingActivity_description_updated:
          output = <div style={{ borderWidth: '2px' }}>{getHtmlJSX(item.value, true, true)}</div>;
          isHTML = true;
          break;
        case AuditTrailActions.RolePermission_updated:
          output = (
            <div>
              <b>{appContext.globalDataCache.roles.get(item.property).name}</b>
              <ul>
                {item.value
                  .split(',')
                  .map((a) => Number(a))
                  .sort((a, b) => sortOnNumber(a, b))
                  .map((a) => (
                    <li key={a}>{getPermissionLabel(a, t)}</li>
                  ))}
              </ul>
            </div>
          );
          isBig = true;
          break;
        case AuditTrailActions.Task_checklist_updated:
          const checklist = new TaskCheckList();
          checklist.fromJSON(item.value);
          if (checklist.items.length > 0) {
            output = (
              <IPStack styles={{ root: { height: 300, width: 300 } }}>
                <CheckList
                  checkList={checklist}
                  allowEdit={false}
                  allowFill={false}
                  allowComment={false}
                  updateCheckList={() => {}}
                />
              </IPStack>
            );
            isBig = true;
          }
          break;
        default:
          output = (
            <IPText>
              {AuditTrailEntry.translateValue(
                item.auditTrailActionId,
                item.value,
                t,
                appContext,
                item.property,
                item.isNewValue,
              )}
            </IPText>
          );
      }
    } catch {
      output = <IPText>{item.value}</IPText>;
    }

    const toolTipComponent = (
      <div
        style={{
          padding: '5px',
          borderRadius: '3px',
        }}
      >
        {output}
      </div>
    );

    // Show copy button for both old and new value rows (not for "change" row)
    const showCopy = item.isNewValue !== undefined && typeof item.value === 'string' && item.value.trim().length > 0;

    // Use correct copied state
    const copied = item.isNewValue ? copiedNew : copiedOld;

    return (
      <IPStack horizontal tokens={globalStackTokensGapSmall}>
        <IPStack.Item
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            height: isHTML ? 40 : 20,
          }}
        >
          <IPTooltipHost content={toolTipComponent} delay={IPTooltipDelay.zero} closeDelay={500}>
            {isBig ? <IPLink>{t('auditTrail:SingleAudit.HoverToSeeMore')}</IPLink> : output}
          </IPTooltipHost>
        </IPStack.Item>
        <IPStack.Item style={{ marginLeft: 'auto' }}>
          {showCopy && (
            <IPIconButton
              iconProps={{ iconName: copied ? 'CheckMark' : 'Copy' }}
              title={copied ? t('auditTrail:SingleAudit.Copied') : t('auditTrail:SingleAudit.Copy')}
              ariaLabel={copied ? t('auditTrail:SingleAudit.Copied') : t('auditTrail:SingleAudit.Copy')}
              onClick={() => copyToClipboard(typeof item.value === 'string' ? item.value : '', item.isNewValue)}
              disabled={copied}
              styles={{ root: { height: 18 } }}
            />
          )}
        </IPStack.Item>
      </IPStack>
    );
  };

  const columns: IColumn[] = [
    {
      key: 'leftColumn',
      minWidth: 80,
      maxWidth: 80,
      name: '',
      onRender: (item?: AuditViewItem, index?: number, column?: IColumn) => {
        if (!item) return <p />;

        return (
          <IPText variant="small" styles={globalTextStylesBold}>
            {item.name}
          </IPText>
        );
      },
    },
    {
      key: 'rightColumn',
      minWidth: 120,
      maxWidth: 400,
      name: '',
      onRender: (item?: AuditViewItem, index?: number, column?: IColumn) => {
        if (item && index && index > 0) {
          return <IPTooltipHost content={item.property}>{onRenderValue(item)}</IPTooltipHost>;
        } else {
          return (
            <IPTooltipHost content={item?.property}>
              <IPText>{item?.value}</IPText>
            </IPTooltipHost>
          );
        }
      },
    },
  ];

  const items: AuditViewItem[] = [
    {
      name: t('auditTrail:SingleAudit.change'),
      value: props.audit.auditTrailAction?.name || '',
      auditTrailActionId: props.audit.auditTrailActionId,
      property: props.audit.property,
      isNewValue: undefined,
    },
    {
      name: t('auditTrail:SingleAudit.oldValue'),
      value: props.audit.oldValue,
      auditTrailActionId: props.audit.auditTrailActionId,
      property: props.audit.property,
      isNewValue: false,
    },
    {
      name: t('auditTrail:SingleAudit.newValue'),
      value: props.audit.newValue,
      auditTrailActionId: props.audit.auditTrailActionId,
      property: props.audit.property,
      isNewValue: true,
    },
  ];

  return (
    <IPStack>
      <IPStack horizontal tokens={globalStackTokensGapSmall}>
        <IPStack.Item styles={{ root: { paddingLeft: 10 } }}>
          {props.audit.createdBy && (
            <IPPersona
              imageUrl={appContext.globalDataCache.users.getPhotoURLObj(props.audit.createdBy.id)}
              text={props.audit.createdBy.name}
              hidePersonaDetails={true}
              size={IPPersonaSize.size24}
            />
          )}
        </IPStack.Item>
        <IPStack.Item grow>
          <IPStack>
            <IPStack horizontal tokens={globalStackTokensGapSmall}>
              <IPStack.Item>
                {props.audit.createdBy && (
                  <IPText variant="small">
                    <strong>{props.audit.createdBy.name}</strong>
                  </IPText>
                )}
              </IPStack.Item>
              <IPStack.Item>
                <IPText variant="small">{toLocaleDateTime(props.audit.created)}</IPText>
              </IPStack.Item>
            </IPStack>
          </IPStack>
        </IPStack.Item>
      </IPStack>
      <IPDetailsList
        compact
        items={items}
        isHeaderVisible={false}
        columns={columns}
        selectionMode={IPSelectionMode.none}
      />
      <IPSeparator />
    </IPStack>
  );
};

export default SingleAudit;
